<template>
  <div
    v-click-outside="{
      closeConditional: shouldCloseDrawer,
      handler: handleClose,
    }"
  >
    <v-card
      right
      fixed
      outlined
      elevation="0"
      class="edition-task-drawer sizing rounded-0 pa-0"
      width="601"
      hide-overlay
      :class="{ 'task-active': visible }"
    >
      <v-progress-linear
        class="loading-bar"
        indeterminate
        :active="handleLoading"
      />
      <div
        v-if="!isForbiddenTask"
        ref="scrollableContainer"
        class="sizing scrollable"
        :class="{
          'grey-background': comments.length,
        }"
      >
        <v-card-text v-if="!isFirstLoading" class="pa-0">
          <TaskDetail
            isEditMode
            :task="task"
            :attachments="attachments"
            :isLoading="isLoading"
            :hasSubscribers="hasSubscribers"
            @changeTask="handleTaskUpdate"
            @updateAttachments="handleUpdateAttachments"
            @removeAttachment="handleRemoveAttachment"
            @delete="showDeleteDialog = true"
            @duplicate="handleDuplicateTask"
            @close="handleClose"
          />

          <div
            v-if="comments.length"
            class="commentsContainer pb-8 pt-1 px-4 mt-n3"
          >
            <CommentsList
              backgroundColor="blue-grey lighten-3"
              :modelType="modelType"
              :modelName="modelName"
              :comments="comments"
              :modelId="taskId"
              :userOrganizationId="organizationId"
              @reply="handleReply"
            />
          </div>
        </v-card-text>

        <v-card-actions
          v-if="!isFirstLoading"
          data-tourid="tasks-creator-comment"
          class="comment-composer px-6 pt-4 pb-5 d-flex justify-center align-start"
        >
          <TaskCommentComposer
            data
            v-if="visible"
            :isLoading="isCreatingComment"
            :replyTo="accountToReply"
            :textareaTestId="textAreaId"
            @submit="onSubmitComment"
          />

          <v-btn
            v-if="showFollowActionable"
            small
            rounded
            text
            class="follow-button mt-5 caption blue-grey--text text--darken-1"
            :elevation="0"
            :loading="isLoadingFollowAction || isLoading"
            :disabled="isLoadingFollowAction || isLoading"
            @click="handleFollow"
          >
            <v-icon size="16" class="mr-2">{{ followButtonIcon }}</v-icon>
            {{ followButtonText }}
          </v-btn>

          <div
            v-if="iAmCreatorOrAssignee && enabledSubscribers.length"
            class="d-flex align-center mt-5"
          >
            <v-tooltip top max-width="320">
              <template v-slot:activator="{ on }">
                <p
                  v-on="on"
                  class="ma-0 pa-0 caption blue-grey--text text--darken-1 mr-2"
                >
                  {{ $t('common.follower_other') }}
                </p>
              </template>
              {{ $t('tasks.follow_message') }}
            </v-tooltip>
            <UsersAvatarList :avatarSize="24" :accounts="enabledSubscribers" />
          </div>
        </v-card-actions>

        <div v-if="isFirstLoading">
          <v-card-text class="px-0">
            <div class="d-flex justify-space-between mx-6 align-center">
              <v-skeleton-loader type="heading" width="400" />
              <div class="d-flex">
                <v-skeleton-loader type="button" width="30" class="mr-1" />
                <v-skeleton-loader type="button" width="30" class="mr-1" />
                <v-skeleton-loader type="button" width="30" class="mr-1" />
                <v-skeleton-loader type="button" width="30" class="mr-1" />
              </div>
            </div>
            <v-divider class="mt-2" />
            <div class="mx-6 mt-6">
              <v-skeleton-loader type="heading" width="800" class="mb-6" />
              <div class="d-flex ml-n3">
                <v-skeleton-loader
                  type="list-item-avatar"
                  width="180"
                  class="mr-2"
                />
                <v-skeleton-loader type="list-item-avatar" width="180" />
              </div>
              <div class="mt-4 d-flex">
                <v-skeleton-loader
                  type="image"
                  width="48%"
                  height="35"
                  class="mr-2"
                />
                <v-skeleton-loader type="image" width="50%" height="35" />
              </div>
              <div class="d-flex mt-4">
                <v-skeleton-loader type="image" width="100%" height="120" />
              </div>
              <div class="d-flex mt-4">
                <v-skeleton-loader
                  type="image"
                  width="80"
                  height="80"
                  class="mr-2"
                />
                <v-skeleton-loader type="image" width="80" height="80" />
              </div>
            </div>
          </v-card-text>

          <v-card-actions class="comment-composer px-6 pt-4 pb-5 d-block">
            <div class="d-flex justify-center align-start w-100">
              <v-skeleton-loader type="image" width="100%" height="80" />
            </div>
            <div class="d-flex justify-space-between mt-2">
              <div class="d-flex">
                <v-skeleton-loader type="avatar" width="70" />
                <v-skeleton-loader type="avatar" width="70" />
              </div>
              <v-skeleton-loader
                type="heading"
                width="150"
                height="60"
                class="d-flex justify-end"
              />
            </div>
          </v-card-actions>
        </div>
      </div>
      <div class="d-flex flex-column align-center justify-center sizing">
        <v-icon color="blue-grey" size="32">mdi-lock</v-icon>
        <p class="body2 mt-2 blue-grey--text text--darken-2">
          {{ $t('tasks.private_task_text') }}
        </p>
        <p class="caption blue-grey--text">
          {{ $t('tasks.you_cannot_see_task') }}
        </p>
      </div>
    </v-card>
    <DeleteConfirmationModal
      :show="showDeleteDialog"
      :isLoading="isDeleting"
      :title="$t('tasks.delete_title')"
      :text="$t('tasks.delete_text')"
      @cancel="showDeleteDialog = false"
      @delete="deleteTask"
    />
    <DeleteConfirmationModal
      :show="showUnfollowDialog"
      :isLoading="isLoadingFollowAction"
      :title="$t('tasks.unfollow_title')"
      :text="$t('tasks.unfollow_text')"
      :confirmText="$t('tasks.unfollow')"
      @cancel="handleCancelUnfollow"
      @delete="subscribe"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState, mapMutations } from 'vuex'
import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import _ from 'lodash'
import { IMAGE_SIZES } from 'rever-media-sdk'
import getEntityId from '@/lib/getEntityId'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'

import TaskDetail from './TaskDetail.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import TaskCommentComposer from './TaskCommentComposer.vue'
import CommentsList from '@/components/Core/Comments/CommentsList.vue'
import UsersAvatarList from '@/components/UsersAvatarList.vue'

const GROUP_OPTIONS = {
  dueDate: 'dueDate',
  priority: 'priority',
  all: 'all',
}
const FORBIDDEN_CODE = 403

export default {
  name: 'TaskEditionDrawer',

  mixins: [mixin, trackerMixin],

  components: {
    TaskDetail,
    DeleteConfirmationModal,
    TaskCommentComposer,
    CommentsList,
    UsersAvatarList,
  },

  data() {
    return {
      IMAGE_SIZES,
      visible: false,
      task: {},
      initialTask: {},
      attachments: [],
      isLoading: false,
      isFirstLoading: false,
      isForbiddenTask: false,
      hasChanges: false,
      taskId: '',
      showDeleteDialog: false,
      isDeleting: false,
      taskToDelete: '',
      accountToReply: null,
      textAreaId: 'commentAddition-textArea-task',
      modelName: 'tasks',
      modelType: this.$t('tasks.task_one'),
      isCreatingComment: false,
      isLoadingFollowAction: false,
      subscription: { isEnabled: false },
      subscriptions: [],
      subscriptionsPageLoading: 0,
      showUnfollowDialog: false,
    }
  },

  async created() {
    eventHub.$on(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATE, this.handleOpenDrawer)
  },

  mounted() {
    if (this.$route.query?.task) {
      this.taskId = this.$route.query?.task
      this.visible = true
      this.fetchTaskById()
      this.fetchAttachmentsById()
      this.getComments()
      this.subscriptions = []
      this.getSubscriptions()
    }
  },

  computed: {
    ...mapGetters(['organizationId', 'account', 'accountId']),
    ...mapState('comments', {
      comments: state => _.orderBy(state?.comments, 'createdAt', 'asc'),
    }),

    hasSubscribers() {
      return (
        Boolean(this.subscription?.isEnabled) ||
        Boolean(this.enabledSubscribers.length)
      )
    },

    hasError() {
      return !this.task?.name?.trim()
    },

    minimizedTaskName() {
      return this.task?.name || this.$t('tasks.new_task')
    },

    hasAttachments() {
      return !_.isEmpty(this.attachments)
    },

    attachmentsIds() {
      return this.attachments?.map?.(file => file.id) ?? []
    },

    taskMediaIds() {
      return this.task?.media?.map?.(media => media.id) ?? null
    },

    handleLoading() {
      return this.isLoading || this.isCreatingComment || this.isFirstLoading
    },

    shouldAutoAssign() {
      return Boolean(this.$route.query.type === 'assignedToMe')
    },

    iAmCreatorOrAssignee() {
      const iAmCreator = this.account.id === this.task.createdBy?.id
      const iAmAssignee = Boolean(
        this.task.assignees?.find(assignee => assignee.id === this.account.id)
      )
      return iAmCreator || iAmAssignee
    },

    showFollowActionable() {
      return !this.iAmCreatorOrAssignee && !this.task.isPrivate
    },

    followButtonText() {
      return this.subscription?.isEnabled
        ? this.$t('tasks.unfollow_task')
        : this.$t('tasks.follow_task')
    },

    followButtonIcon() {
      return this.subscription?.isEnabled
        ? 'mdi-bell-off-outline'
        : 'mdi-bell-outline'
    },

    subscribers() {
      const subscribers = this.subscriptions.map(
        subscription => subscription.account
      )
      return _.uniqBy(subscribers, 'id')
    },

    enabledSubscribers() {
      const enabledSubscriptions = this.subscriptions.filter(
        subscription => subscription.isEnabled
      )

      const subscribers = enabledSubscriptions.map(
        subscription => subscription.account
      )

      return _.uniqBy(subscribers, 'id')
    },
  },

  methods: {
    ...mapActions({
      postComment: 'comments/postComment',
      getCommentsByModelId: 'comments/getCommentsByModelId',
    }),

    ...mapMutations({
      resetComments: 'comments/resetComments',
    }),

    handleCancelUnfollow() {
      this.trackTasks('tasks.unfollowDialogCancel')
      this.showUnfollowDialog = false
    },

    shouldCloseDrawer(event) {
      const taskItemContainer = event?.target?.closest?.('.task-item-row')
      const taskCreatorContainer = event?.target?.closest?.(
        '.creation-task-drawer'
      )
      const commentEmojiContainer = event?.target?.closest?.('.emojicontainer')
      const commentAccountSelector = event?.target?.closest?.(
        '.comment-account-selector'
      )
      const commentAccountSelectorTooltipInner = event?.target?.closest?.(
        '.tooltip-inner'
      )

      const mediaManagerDialog = event?.target?.closest?.(
        '.media-manager-gallery'
      )

      const taskCreatorDatePicker = event?.target?.closest?.(
        '.tasks-creator-due-date'
      )

      const taskCreatorPriorityPicker = event?.target?.closest?.(
        '.v-select-list'
      )

      const confirmationDialog = event?.target?.closest?.(
        '.confirmation-dialog'
      )
      const statusMenu = event?.target?.closest?.('.rever-menu')

      const elementIsTask = Boolean(taskItemContainer?.contains?.(event.target))

      const assigneesMenu = event?.target.closest?.('.entity-search-popover')

      const moreFollowersMenu = event?.target.closest?.('.more-users-list')

      return (
        this.visible &&
        !elementIsTask &&
        !taskCreatorContainer &&
        !commentEmojiContainer &&
        !commentAccountSelector &&
        !commentAccountSelectorTooltipInner &&
        !mediaManagerDialog &&
        !taskCreatorDatePicker &&
        !taskCreatorPriorityPicker &&
        !confirmationDialog &&
        !statusMenu &&
        !assigneesMenu &&
        !moreFollowersMenu
      )
    },

    async fetchTaskById() {
      try {
        this.isFirstLoading = true
        const { data } = await api.tasks.getById(
          this.organizationId,
          this.taskId
        )

        this.task = data
        this.initialTask = data
      } catch (err) {
        if (err.response.status === FORBIDDEN_CODE) {
          this.isForbiddenTask = true
          return
        }
        this.handleRequestError(err)
      } finally {
        this.isFirstLoading = false
      }
    },

    async fetchAttachmentsById() {
      try {
        const { data } = await api.tasks.getAttachments(
          this.organizationId,
          this.taskId
        )
        this.attachments = data?.map?.(attachment => {
          return { ...attachment.media, attachmentId: attachment.id }
        })
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async updateTask(payload) {
      try {
        if (this.hasError) return
        this.isLoading = true
        await api.tasks.update(this.organizationId, this.taskId, payload)

        this.isLoading = false

        const taskPayloadMixpanel = _.omit(
          {
            ...payload,
            assignees: getEntityId(payload?.assignees?.[0]) ?? [],
            media: payload?.media,
          },
          ['site', 'tags']
        )

        this.trackTasks('tasks.update', {
          taskId: this.taskId,
          updatedField: taskPayloadMixpanel,
        })
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    debounceUpdate: _.debounce(function(payload) {
      this.updateTask(payload)
    }, 1000),

    handleTaskUpdate(taskProperty, value) {
      this.task = {
        ...this.task,
        [taskProperty]: value,
      }

      this.hasChanges = true

      if (taskProperty === 'assignees') {
        return this.updateTask({
          assignees: value?.[0]?.id ? [value?.[0]?.id] : [],
          site: value?.[0]?.site?._id ?? this.account?.site?._id,
        })
      }

      if (taskProperty === 'media') {
        return this.updateTask({
          media: this.taskMediaIds,
        })
      }

      return this.debounceUpdate({ [taskProperty]: value })
    },

    async addAttachmentsToDuplicatedTask(taskId) {
      this.attachmentsIds?.forEach(id => {
        api.tasks.attach(this.organizationId, taskId, { media: id })
      })
    },

    async handleDuplicateTask() {
      try {
        this.isFirstLoading = true
        const tags = this.task?.tags?.map?.(getEntityId) ?? []
        const site = this.task?.assignees?.[0]?.site ?? this.account?.site
        const media = _.isEmpty(this.task?.media)
          ? []
          : this.task?.media?.map(getEntityId)

        const { data } = await api.tasks.create(
          this.organizationId,
          _.omit(
            {
              ..._.omitBy(this.task, _.isNull),
              assignees: this.shouldAutoAssign
                ? [getEntityId(this.account)]
                : [],
              status: this.$constants.tasksStatus.open,
              tags: tags,
              site: getEntityId(site),
              name: `${this.task.name} ${this.$t('tasks.copy')}`,
              media: media,
            },
            'id'
          )
        )

        if (!_.isEmpty(this.attachmentsIds)) {
          this.addAttachmentsToDuplicatedTask(data.id)
        }

        this.replaceRoute({
          path: this.$route.fullPath,
          query: { ...this.$route.query, task: getEntityId(data) },
        })
        this.emitOnUpdateTask()
        this.trackTasks('tasks.duplicate', {
          from: 'editDrawer',
        })
        this.handleClose()
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    afterLeaveAnimation() {
      this.setDefaultTask()
    },

    async updateAttachments(newFile) {
      const { data } = await api.tasks.attach(
        this.organizationId,
        this.taskId,
        {
          media: newFile.id,
        }
      )
      this.attachments = this.attachments.map(file =>
        file.id === data.media ? { ...file, attachmentId: data.id } : file
      )

      this.trackTasks('tasks.update', {
        taskId: this.taskId,
        updatedField: {
          attachments: this.attachments.map(file => file?.id),
        },
      })
    },

    async deleteAttachments(attachmentId) {
      api.attachment.delete({
        organizationId: this.organizationId,
        attachmentId: attachmentId,
      })
    },

    async handleRemoveAttachment({ fileId, attachmentId }) {
      this.attachments = this.attachments.filter(f => f.id !== fileId)
      this.deleteAttachments(attachmentId)
      this.trackTasks('tasks.update', {
        taskId: this.taskId,
        updatedField: {
          attachments: this.attachments
            .filter(f => f.id !== fileId)
            .map(file => file.id),
        },
      })
    },

    handleUpdateAttachments(newFile) {
      this.attachments = [...this.attachments, newFile]
      this.updateAttachments(newFile)
    },

    async deleteTask() {
      try {
        if (!this.taskId) return
        this.isDeleting = true
        await api.tasks.delete(this.organizationId, this.taskId)
        this.emitOnUpdateTask('deleted')
        this.setDefaultTask()
        this.setDefaultUrl()
        this.trackTasks('tasks.delete', {
          taskId: this.taskId,
          from: 'editDrawer',
        })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isDeleting = false
        this.showDeleteDialog = false
        this.visible = false
        this.taskId = ''
      }
    },

    setDefaultTask() {
      this.task = {}
      this.attachments = []
      this.initialTask = {}
      this.isForbiddenTask = false
    },

    setDefaultUrl() {
      if (!this.$route.query?.task) return

      const savedScrollY = window.scrollY
      this.replaceRoute({
        path: this.$route.path,
        query: { ...this.$route.query, task: undefined, groupKey: undefined },
      })
      this.$nextTick(() => {
        window.scrollTo(0, savedScrollY)
      })
    },

    handleClose() {
      if (this.hasChanges) this.emitOnUpdateTask()
      this.hasChanges = false
      this.visible = false
      this.setDefaultUrl()
      this.trackTasks('tasks.close', {
        from: 'edit',
      })
    },

    handleReply(account) {
      this.accountToReply = { ...account }
    },

    async subscribeMentions(mentions) {
      const accountsToSubscribe = [...mentions, this.accountId]
      const creator = [this.task?.createdBy?.id]
      const assignees = this.task?.assignees?.map(assignee => assignee.id)
      const currentSubcribers = this.subscribers?.map(
        subscriber => subscriber.id
      )
      const accountsToExclude = _.compact([
        ...creator,
        ...assignees,
        ...currentSubcribers,
      ])

      const newSubscriptions = _.difference(
        accountsToSubscribe,
        accountsToExclude
      )
      if (!newSubscriptions.length) return
      await api.subscription.createBulk(
        this.organizationId,
        this.modelName,
        this.taskId,
        _.uniq(newSubscriptions)
      )
      this.subscriptions = []
      await this.getSubscriptions()
    },

    async onSubmitComment(comment) {
      if (!comment.message) return

      const payload = {
        content: comment.message,
        accounts: comment.accountIds,
      }

      try {
        this.isCreatingComment = true
        await api.comment.postComment(
          this.organizationId,
          this.modelName,
          this.taskId,
          payload
        )

        if (!this.task.isPrivate) {
          await this.subscribeMentions(comment.accountIds)
        }

        await this.getComments()
        const targetRef = this.$refs.scrollableContainer

        this.$nextTick(() => {
          targetRef.scrollTo({
            top: targetRef.scrollHeight,
            left: 0,
            behavior: 'smooth',
          })
        })
        this.trackTasks('tasks.comment', { taskId: this.taskId })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.accountToReply = null
        this.isCreatingComment = false
      }
    },

    async getComments() {
      try {
        this.isLoading = true
        await this.getCommentsByModelId({
          organizationId: this.organizationId,
          modelName: this.modelName,
          modelId: this.taskId,
        })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    emitOnUpdateTask(deleted) {
      if (
        this.$route.query.viewType === this.$constants.TASKS_VIEW_OPTIONS.kanban
      ) {
        const event = deleted ? 'DELETED' : 'UPDATED'
        const payload = deleted
          ? this.initialTask
          : {
              ...this.task,
              oldStatus: this.initialTask.status,
              newStatus: this.task.status,
            }
        return eventHub.$emit(HUB_EVENTS.TASK_EDITION_DRAWER[event], payload)
      }

      if (
        this.$route.query.group === GROUP_OPTIONS.priority &&
        this.task.priority !== this.initialTask.priority
      )
        return eventHub.$emit(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATED)
      if (
        this.$route.query.group === GROUP_OPTIONS.dueDate &&
        this.task.dueDate !== this.initialTask.dueDate
      )
        return eventHub.$emit(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATED)

      if (this.$route.name === 'board') {
        const task = deleted ? null : this.task
        return eventHub.$emit(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATED, task)
      }

      if (this.$route.name === 'home') {
        const event = deleted ? 'DELETED' : 'UPDATED'

        eventHub.$emit(HUB_EVENTS.TASK_EDITION_DRAWER[event], this.task)
        return
      }

      return eventHub.$emit(
        HUB_EVENTS.TASK_EDITION_DRAWER.UPDATED,
        this.$route.query.groupKey
      )
    },

    handleOpenDrawer(taskId) {
      if (this.hasChanges) this.emitOnUpdateTask()
      this.setDefaultTask()
      this.taskId = taskId || this.$route.query?.task
      this.visible = true
      this.fetchTaskById()
      this.fetchAttachmentsById()
      this.getComments()
      this.subscriptionsPageLoading = 0
      this.subscriptions = []
      this.getSubscriptions()
    },

    async getSubscriptions() {
      try {
        this.isLoadingFollowAction = true
        const itemsPerPage = 25
        const skip = this.subscriptionsPageLoading * itemsPerPage
        const { data } = await api.subscription.getSubscriptions({
          organizationId: this.organizationId,
          model: this.modelName,
          modelId: this.taskId,
          skip,
          limit: itemsPerPage,
        })

        const results = data?.results ?? []

        this.subscriptions.push(...results)

        if (data?.totalRecordsInData > this.subscriptions.length) {
          this.subscriptionsPageLoading += 1
          return this.getSubscriptions()
        }

        this.subscription = this.subscriptions.find(
          subscription => subscription?.account?.id === this.account.id
        )
        this.isLoadingFollowAction = false
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    handleFollow() {
      if (this.subscription?.isEnabled) {
        this.trackTasks('tasks.unfollowDialogOpen')
        this.showUnfollowDialog = true
      } else {
        this.subscribe()
      }
    },

    async subscribe() {
      try {
        this.isLoadingFollowAction = true
        if (this.subscription?.id) {
          await api.subscription.update(
            this.organizationId,
            this.subscription.id,
            this.modelName,
            this.task.id,
            {
              isEnabled: !this.subscription?.isEnabled,
            }
          )
          this.subscription.isEnabled = !this.subscription?.isEnabled
          this.trackTasks('tasks.updateFollow', {
            newFollowStatus: !this.subscription?.isEnabled,
          })
        } else {
          const { data } = await api.subscription.create(
            this.organizationId,
            this.modelName,
            this.task.id,
            {
              entityId: this.task.id,
              entityType: 'task',
              account: this.account.id,
            }
          )
          this.subscription = data
          this.trackTasks('tasks.createFollow')
        }
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.showUnfollowDialog = false
        this.isLoadingFollowAction = false
        if (this.subscription.isEnabled) {
          this.showSuccess(this.$t('tasks.task_followed_successfully'))
        } else {
          this.showSuccess(this.$t('tasks.task_unfollowed_successfully'))
        }
      }
    },
  },

  beforeDestroy() {
    this.resetComments()
    this.visible = false
    eventHub.$off(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATE)
  },
}
</script>
<style lang="scss" scoped>
.edition-task-drawer {
  box-shadow: $elevation;
  bottom: 0;
  right: -601px;
  width: 601px;
  position: fixed;
  z-index: 7;
  transition: right 0.2s ease-in-out;
}
.task-active {
  right: 0;
}
.scrollable {
  overflow: auto;
  padding-bottom: 200px !important;
}

.sizing {
  height: calc(100vh - 64px);
}

.comment-composer {
  position: absolute;
  width: 100%;
  bottom: 0;
  background-color: $color-white;
  border-top: 1px solid $color-blue-grey-lighten2;
  flex-direction: column;
}

.loading-bar {
  z-index: 3;
  position: absolute;
}

.commentsContainer {
  background-color: $color-blue-grey-lighten3;
  border-top: 1px solid $color-blue-grey-lighten2;
}

.grey-background {
  background-color: $color-blue-grey-lighten3;
}

.follow-button.v-btn:hover {
  background-color: $color-blue-lighten2 !important;
  color: $color-blue !important;
}
</style>
